<template>
  <div class="checkout-summary">
    <section class="checkout-product" aria-label="购买内容">
      <div class="checkout-product__heading">
        <i :class="productIcon" aria-hidden="true" />
        <div>
          <h3>{{ quote.product.name }}</h3>
          <p v-if="quote.product.description">{{ quote.product.description }}</p>
        </div>
      </div>
      <div class="checkout-product__meta">
        <span>购买数量 × {{ quote.quantity }}</span>
        <span>单价 ¥ {{ (quote.product.amountMinor / 100).toFixed(2) }}</span>
      </div>
      <details v-if="benefits.length" class="checkout-benefit-details" :open="!compact">
        <summary>包含的权益<i class="ri-arrow-down-s-line" aria-hidden="true" /></summary>
        <dl class="checkout-benefits">
          <div v-for="benefit in benefits" :key="benefit.label">
            <dt>{{ benefit.label }}</dt>
            <dd>{{ benefit.value }}</dd>
          </div>
        </dl>
      </details>
    </section>

    <CouponRedemptionGuide :disabled="disabled" @redeem="emit('redeem')" />

    <section class="checkout-coupon" aria-label="优惠选择">
      <div class="checkout-coupon__heading">
        <label :for="couponSelectId">优惠券</label>
      </div>
      <a-select
        :id="couponSelectId"
        v-model:value="couponId"
        :disabled="disabled || !quote.coupons.length"
        allow-clear
        placeholder="不使用优惠券"
        @change="emit('change')"
      >
        <a-select-option v-for="coupon in quote.coupons" :key="coupon.id" :value="coupon.id">
          {{ coupon.name }} · -¥ {{ (coupon.discountAmountMinor / 100).toFixed(2) }}
        </a-select-option>
      </a-select>
      <p>
        {{
          quote.coupons.length ? '选择优惠券后，费用会重新计算。' : '暂无适用于本次购买的优惠券。'
        }}
      </p>
    </section>

    <dl class="checkout-totals" aria-live="polite">
      <div>
        <dt>商品总额</dt>
        <dd>¥ {{ (quote.originalAmountMinor / 100).toFixed(2) }}</dd>
      </div>
      <div>
        <dt>优惠抵扣</dt>
        <dd>- ¥ {{ (quote.discountAmountMinor / 100).toFixed(2) }}</dd>
      </div>
      <div class="checkout-totals__payable">
        <dt>应付金额</dt>
        <dd>¥ {{ (quote.amountMinor / 100).toFixed(2) }}</dd>
      </div>
    </dl>
    <p class="checkout-note">
      <i class="ri-information-line" aria-hidden="true" />支付成功后，权益将发放到当前账号。
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, useId } from 'vue';
import CouponRedemptionGuide from './CouponRedemptionGuide.vue';
import { useCompactViewport } from '@/composables/useCompactViewport';
import type { OrderQuoteView } from 'shared';

const props = defineProps<{ quote: OrderQuoteView; disabled?: boolean }>();
const couponId = defineModel<string | null>('couponId', { required: true });
const emit = defineEmits<{ change: []; redeem: [] }>();
const couponSelectId = useId();
const compact = useCompactViewport();
const productIcon = computed(
  () =>
    ({
      pro_plan: 'ri-vip-crown-2-line',
      team_plan: 'ri-team-line',
      team_seat_pack: 'ri-user-add-line',
      credit_pack: 'ri-flashlight-line',
      storage_pack: 'ri-hard-drive-3-line',
      record_pack: 'ri-database-2-line',
    })[props.quote.product.kind],
);
const benefits = computed(() => {
  const { product, quantity } = props.quote;
  const { grant, kind } = product;
  const items: Array<{ label: string; value: string }> = [];
  if (kind === 'credit_pack') {
    items.push({ label: 'AI 积分', value: `${(grant.credits ?? 0).toLocaleString()} Credits` });
    items.push({ label: '积分有效期', value: `${grant.creditsExpiryDays ?? 365} 天` });
  } else if (grant.durationDays !== undefined) {
    items.push({ label: '权益有效期', value: `${grant.durationDays} 天` });
  }
  if (grant.capacity) {
    items.push({
      label: '工作台上限',
      value: `${grant.capacity.maxWorkspaces.toLocaleString()} 个`,
    });
    items.push({
      label: '组件实例上限',
      value: `${grant.capacity.maxComponentInstances.toLocaleString()} 个`,
    });
  }
  if (grant.quota) {
    items.push({ label: '存储空间', value: bytes(grant.quota.storageBytes) });
    items.push({ label: '月下载流量', value: bytes(grant.quota.transferBytes) });
    items.push({ label: '数据记录', value: `${grant.quota.recordCount.toLocaleString()} 条` });
    items.push({
      label: '月度 AI 积分',
      value: `${grant.quota.aiCredits.toLocaleString()} Credits`,
    });
  }
  if (grant.seatCount !== undefined)
    items.push({
      label: '团队席位',
      value: `${(grant.seatCount * (kind === 'team_seat_pack' ? quantity : 1)).toLocaleString()} 个`,
    });
  if (kind === 'storage_pack')
    items.push({ label: '新增存储空间', value: bytes((grant.storageBytes ?? 0) * quantity) });
  if (kind === 'record_pack')
    items.push({
      label: '新增数据记录',
      value: `${((grant.recordCount ?? 0) * quantity).toLocaleString()} 条`,
    });
  return items;
});
function bytes(value: number): string {
  return value >= 1024 ** 3
    ? `${(value / 1024 ** 3).toFixed(1)} GB`
    : value >= 1024 ** 2
      ? `${(value / 1024 ** 2).toFixed(1)} MB`
      : value >= 1024
        ? `${(value / 1024).toFixed(1)} KB`
        : `${value} B`;
}
</script>

<style scoped>
.checkout-summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-areas: 'product guide' 'product coupon' 'product totals' 'product note';
  gap: 16px 24px;
  color: var(--aw-text);
}
.checkout-product {
  grid-area: product;
  align-self: start;
  padding: 16px;
  border-radius: var(--aw-radius-md);
  background: var(--aw-surface-subtle);
}
.checkout-product__heading {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.checkout-product__heading > i {
  flex-shrink: 0;
  color: var(--aw-primary);
  font-size: 24px;
  line-height: 1.4;
}
.checkout-product__heading > div {
  min-width: 0;
}
.checkout-product h3 {
  margin: 0;
  font-size: 18px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.checkout-product p,
.checkout-coupon p,
.checkout-note {
  margin: 4px 0 0;
  color: var(--aw-text-secondary);
  font-size: 12px;
  line-height: 1.7;
}
.checkout-product__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-top: 12px;
  color: var(--aw-text-secondary);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.checkout-benefits {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 20px;
  margin: 12px 0 0;
}
.checkout-benefits dt {
  color: var(--aw-text-secondary);
  font-size: 12px;
}
.checkout-benefits dd {
  margin: 4px 0 0;
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
.checkout-coupon {
  grid-area: coupon;
}
.redemption-guide {
  grid-area: guide;
}
.checkout-benefit-details {
  margin-top: 16px;
  padding-top: 8px;
  border-top: 1px solid var(--aw-border);
}
.checkout-benefit-details summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 36px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  list-style: none;
}
.checkout-benefit-details summary::-webkit-details-marker {
  display: none;
}
.checkout-benefit-details summary:focus-visible {
  outline: 2px solid var(--aw-primary);
  outline-offset: 3px;
  border-radius: var(--aw-radius-xs);
}
.checkout-benefit-details[open] summary i {
  transform: rotate(180deg);
}
.checkout-coupon__heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.checkout-coupon label {
  font-weight: 600;
}
.checkout-coupon :deep(.ant-select) {
  width: 100%;
}
.checkout-totals {
  grid-area: totals;
  display: grid;
  gap: 8px;
  margin: 0;
  padding-top: 16px;
  border-top: 1px solid var(--aw-border);
}
.checkout-totals div {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
}
.checkout-totals dt {
  color: var(--aw-text-secondary);
}
.checkout-totals dd {
  margin: 0;
  font-variant-numeric: tabular-nums;
}
.checkout-totals .checkout-totals__payable {
  margin-top: 4px;
}
.checkout-totals__payable dt {
  color: var(--aw-text);
  font-weight: 600;
}
.checkout-totals__payable dd {
  color: var(--aw-primary);
  font-size: 26px;
  font-weight: 700;
}
.checkout-note {
  grid-area: note;
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin: 0;
}
@media (max-width: 720px) {
  .checkout-summary {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'product' 'guide' 'coupon' 'totals' 'note';
    gap: 12px;
  }
  .checkout-benefit-details {
    margin-top: 12px;
  }
  .checkout-product__meta {
    margin-top: 8px;
  }
  .checkout-benefit-details summary {
    min-height: 44px;
  }
  .checkout-coupon__heading {
    margin-bottom: 4px;
  }
  .checkout-product {
    padding: 12px;
  }
  .checkout-benefits {
    gap: 12px;
  }
}
</style>
